import '../playerTransportUtilities.css';
import '../playerOptionsSheet.css';
import { appStore } from '../core/store';
import {
  MARKER_LEAD_IN_BEAT_OPTIONS,
  MARKER_LEAD_IN_OPTIONS,
  resolveEffectiveLeadIn,
} from '../core/types';

const CLOCK_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="2"/>
    <path d="M12 7v5l3.4 2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

const METRONOME_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 4h6l3 16H6L9 4Z" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"/>
    <path d="M12 7l2.4 5.2" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>
    <path d="M9.3 16h5.4" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>
  </svg>`;

interface PlayerContext {
  setlist: ReturnType<typeof appStore.getState>['setlists'][number] | null;
  song: ReturnType<typeof appStore.getState>['songs'][number] | null;
}

function activePlayerContext(): PlayerContext {
  const state = appStore.getState();
  const setlist = state.setlists.find((candidate) => candidate.id === state.settings.activeSetlistId) ?? null;
  const entry = setlist?.entries[state.settings.activeEntryIndex];
  const song = entry ? state.songs.find((candidate) => candidate.id === entry.songId) ?? null : null;
  return { setlist, song };
}

function countInIsActive(): boolean {
  const { setlist, song } = activePlayerContext();
  if (!song) return false;
  return resolveEffectiveLeadIn(setlist?.countInOverride ?? null, song).seconds > 0;
}

function songHasClick(): boolean {
  const { song } = activePlayerContext();
  return song != null && (song.mode === 'metronome' || song.syntheticClickEnabled);
}

function createDrawer(container: HTMLElement): HTMLDivElement {
  const host = container.querySelector<HTMLElement>('.performance') ?? container;
  const existing = host.querySelector<HTMLDivElement>('#player-utility-drawer');
  if (existing) return existing;

  const drawer = document.createElement('div');
  drawer.id = 'player-utility-drawer';
  drawer.className = 'player-utility-drawer';
  drawer.hidden = true;
  drawer.innerHTML = `
    <button type="button" class="player-utility-drawer-scrim" aria-label="Close player option drawer"></button>
    <section class="player-utility-drawer-sheet" role="dialog" aria-modal="true" aria-labelledby="player-utility-drawer-title">
      <div class="player-utility-drawer-handle" aria-hidden="true"></div>
      <div class="player-utility-drawer-heading">
        <h2 id="player-utility-drawer-title"></h2>
        <button type="button" class="player-utility-drawer-close" aria-label="Close">×</button>
      </div>
      <div class="player-utility-drawer-options"></div>
    </section>`;
  host.appendChild(drawer);
  return drawer;
}

export function enhanceCompactPlayerTransport(container: HTMLElement): () => void {
  const row = container.querySelector<HTMLElement>('#transport-row-compact');
  const countIn = container.querySelector<HTMLButtonElement>('#player-count-in-status-compact');
  const loop = container.querySelector<HTMLButtonElement>('#loop-btn-compact');
  const loopLabel = container.querySelector<HTMLElement>('#loop-mode-label-compact');
  const previous = container.querySelector<HTMLButtonElement>('#prev-song-btn-compact');
  const play = container.querySelector<HTMLButtonElement>('#play-pause-btn-compact');
  const next = container.querySelector<HTMLButtonElement>('#next-song-btn-compact');
  const more = container.querySelector<HTMLButtonElement>('#click-settings-btn-compact');
  const legacyMenu = container.querySelector<HTMLDivElement>('#click-sound-menu-compact');

  if (!row || !countIn || !loop || !previous || !play || !next || !more) return () => {};

  let click = container.querySelector<HTMLButtonElement>('#click-status-btn-compact');
  if (!click) {
    click = document.createElement('button');
    click.type = 'button';
    click.id = 'click-status-btn-compact';
    click.className = 'player-transport-utility player-click-status-btn';
  }

  const drawer = createDrawer(container);
  const drawerTitle = drawer.querySelector<HTMLElement>('#player-utility-drawer-title')!;
  const drawerOptions = drawer.querySelector<HTMLElement>('.player-utility-drawer-options')!;
  const drawerClose = drawer.querySelector<HTMLButtonElement>('.player-utility-drawer-close')!;
  const drawerScrim = drawer.querySelector<HTMLButtonElement>('.player-utility-drawer-scrim')!;

  let suppressLoopIntercept = false;
  let suppressMoreIntercept = false;
  let drawerReturnFocus: HTMLElement | null = null;

  const closeDrawer = (): void => {
    drawer.hidden = true;
    drawer.classList.remove('is-open');
    drawerReturnFocus?.focus();
    drawerReturnFocus = null;
  };

  const openDrawer = (title: string, content: string, trigger?: HTMLElement): void => {
    drawerReturnFocus = trigger ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    drawerTitle.textContent = title;
    drawerOptions.innerHTML = content;
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    requestAnimationFrame(() => drawerOptions.querySelector<HTMLElement>('button, input')?.focus() ?? drawerClose.focus());
  };

  const buildCountInDrawer = (): string => {
    const { setlist, song } = activePlayerContext();
    if (!song) return '<p class="player-utility-drawer-note">No song loaded.</p>';

    const effective = resolveEffectiveLeadIn(setlist?.countInOverride ?? null, song);
    const override = setlist?.countInOverride ?? null;
    const hasKnownBpm = song.mode === 'metronome' || song.syntheticClickEnabled;
    const options: string[] = [];

    const option = (label: string, attrs: string, selected: boolean): string =>
      `<button type="button" class="player-utility-option${selected ? ' is-selected' : ''}" ${attrs}><span>${label}</span><span class="player-utility-option-check">✓</span></button>`;

    options.push(option('Off', 'data-player-count-in="off"', effective.seconds <= 0));
    if (setlist) options.push(option('Song default', 'data-player-count-in="song"', override === null && effective.seconds > 0));

    if (hasKnownBpm) {
      for (const beats of MARKER_LEAD_IN_BEAT_OPTIONS) {
        const selected = setlist
          ? override !== null && typeof override === 'object' && override.beats === beats
          : song.markerLeadInBeats === beats;
        options.push(option(`${beats} count`, `data-player-count-in="beats" data-value="${beats}"`, selected));
      }
    } else {
      for (const seconds of MARKER_LEAD_IN_OPTIONS) {
        const selected = setlist
          ? override !== null && typeof override === 'object' && override.beats === null && override.seconds === seconds
          : song.markerLeadInBeats === null && song.markerLeadInSeconds === seconds;
        options.push(option(`${seconds} seconds`, `data-player-count-in="seconds" data-value="${seconds}"`, selected));
      }
    }

    return options.join('');
  };

  const loopMode = (): string => loopLabel?.textContent?.trim() || 'Off';

  const buildLoopDrawer = (): string => {
    const current = loopMode();
    const modes = ['Off', 'Section', 'Song', 'Setlist'];
    return modes.map((mode) => `
      <button type="button" class="player-utility-option${current === mode ? ' is-selected' : ''}" data-loop-target="${mode}">
        <span>${mode === 'Off' ? 'Off' : mode === 'Section' ? 'Current section' : mode === 'Song' ? 'Entire song' : 'Entire setlist'}</span>
        <span class="player-utility-option-check">✓</span>
      </button>`).join('');
  };

  const buildPlayerOptionsDrawer = (): string => {
    const { setlist, song } = activePlayerContext();
    const canPracticeSpeed = !!song && song.mode !== 'metronome' && setlist?.mode !== 'live';
    if (!canPracticeSpeed) {
      return '<p class="player-utility-drawer-note">No additional player options are available for this song.</p>';
    }
    const legacySlider = legacyMenu?.querySelector<HTMLInputElement>('.practice-speed-slider');
    const percent = Number(legacySlider?.value ?? 100);
    return `
      <section class="player-options-section" aria-labelledby="player-options-practice-heading">
        <div class="player-options-section-label" id="player-options-practice-heading">Practice</div>
        <div class="player-options-speed-heading">
          <span>Practice speed</span>
          <strong class="player-options-speed-readout${percent === 100 ? '' : ' is-modified'}">${percent}%</strong>
        </div>
        <div class="player-options-speed-control">
          <button type="button" class="player-options-speed-step" data-player-speed-step="-5" aria-label="Decrease practice speed by 5 percent">−5</button>
          <input type="range" class="player-options-speed-slider" min="50" max="120" step="1" value="${percent}" aria-label="Practice speed" />
          <button type="button" class="player-options-speed-step" data-player-speed-step="5" aria-label="Increase practice speed by 5 percent">+5</button>
        </div>
        <p class="player-options-help">Changes speed without changing pitch. Resets to 100% when the next song loads.</p>
      </section>`;
  };

  const buildClickDrawer = (): string => {
    if (!legacyMenu) return '<p class="player-utility-drawer-note">No click options are available.</p>';

    const presetButtons = Array.from(legacyMenu.querySelectorAll<HTMLButtonElement>('.click-sound-option'));
    const manageButton = legacyMenu.querySelector<HTMLButtonElement>('[data-action="manage-click-presets"]');
    const emptyMessage = legacyMenu.querySelector<HTMLElement>('.player-header-menu-empty')?.textContent?.trim();

    const presetRows = presetButtons.map((button, index) => {
      const label = button.querySelector<HTMLElement>('span')?.textContent?.trim() || button.textContent?.trim() || `Preset ${index + 1}`;
      const detail = button.querySelector<HTMLElement>('small')?.textContent?.trim();
      return `
        <button type="button" class="player-utility-option${button.classList.contains('is-selected') ? ' is-selected' : ''}" data-click-proxy-index="${index}">
          <span>${label}${detail ? `<small class="player-utility-option-detail">${detail}</small>` : ''}</span>
          <span class="player-utility-option-check">✓</span>
        </button>`;
    }).join('');

    const empty = presetRows ? '' : `<p class="player-utility-drawer-note">${emptyMessage || 'No saved click presets yet.'}</p>`;
    const manage = manageButton
      ? '<button type="button" class="player-utility-option player-utility-action" data-click-manage-presets><span>Manage presets in Metronome</span><span aria-hidden="true">›</span></button>'
      : '';

    return `${presetRows}${empty}${manage}`;
  };

  const setLoopMode = async (target: string): Promise<void> => {
    if (loopMode() === target) return;
    for (let i = 0; i < 5 && loopMode() !== target; i += 1) {
      suppressLoopIntercept = true;
      loop.click();
      suppressLoopIntercept = false;
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }
  };

  const onCountInCapture = (event: Event): void => {
    event.preventDefault();
    event.stopImmediatePropagation();
    openDrawer('Count-in', buildCountInDrawer(), countIn);
  };

  const onLoopCapture = (event: Event): void => {
    if (suppressLoopIntercept) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openDrawer('Loop', buildLoopDrawer(), loop);
  };

  const openLegacyClickMenu = (): void => {
    suppressMoreIntercept = true;
    more.click();
    suppressMoreIntercept = false;
  };

  const openClickSettings = (): void => {
    openLegacyClickMenu();
    if (legacyMenu) legacyMenu.hidden = true;
    more.setAttribute('aria-expanded', 'false');
    openDrawer('Click', buildClickDrawer(), click);
  };

  const onMoreCapture = (event: Event): void => {
    if (suppressMoreIntercept) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    // Render the legacy menu hidden first so its existing practice-speed
    // slider reflects the controller's live value. Player Options proxies
    // to that proven input handler instead of changing audio semantics.
    openLegacyClickMenu();
    if (legacyMenu) legacyMenu.hidden = true;
    more.setAttribute('aria-expanded', 'false');
    openDrawer('Player Options', buildPlayerOptionsDrawer(), more);
  };

  const syncPlayerOptionsSpeed = (percent: number): void => {
    const slider = legacyMenu?.querySelector<HTMLInputElement>('.practice-speed-slider');
    if (!slider) return;
    slider.value = String(percent);
    slider.dispatchEvent(new Event('input', { bubbles: true }));
    const readout = drawer.querySelector<HTMLElement>('.player-options-speed-readout');
    const visibleSlider = drawer.querySelector<HTMLInputElement>('.player-options-speed-slider');
    if (readout) {
      readout.textContent = `${percent}%`;
      readout.classList.toggle('is-modified', percent !== 100);
    }
    if (visibleSlider) visibleSlider.value = String(percent);
  };

  const onDrawerInput = (event: Event): void => {
    const slider = event.target;
    if (!(slider instanceof HTMLInputElement) || !slider.matches('.player-options-speed-slider')) return;
    syncPlayerOptionsSpeed(Number(slider.value));
  };

  const onDrawerClick = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const loopTarget = target.closest<HTMLButtonElement>('[data-loop-target]');
    if (loopTarget?.dataset.loopTarget) {
      event.preventDefault();
      void setLoopMode(loopTarget.dataset.loopTarget).then(closeDrawer);
      return;
    }

    const clickProxy = target.closest<HTMLButtonElement>('[data-click-proxy-index]');
    if (clickProxy?.dataset.clickProxyIndex) {
      const index = Number(clickProxy.dataset.clickProxyIndex);
      legacyMenu?.querySelectorAll<HTMLButtonElement>('.click-sound-option')[index]?.click();
      closeDrawer();
      return;
    }

    if (target.closest('[data-click-manage-presets]')) {
      legacyMenu?.querySelector<HTMLButtonElement>('[data-action="manage-click-presets"]')?.click();
      closeDrawer();
      return;
    }

    const speedStep = target.closest<HTMLButtonElement>('[data-player-speed-step]');
    if (speedStep) {
      const current = Number(drawer.querySelector<HTMLInputElement>('.player-options-speed-slider')?.value ?? 100);
      const nextPercent = Math.min(120, Math.max(50, current + Number(speedStep.dataset.playerSpeedStep)));
      syncPlayerOptionsSpeed(nextPercent);
      return;
    }

    const countOption = target.closest<HTMLButtonElement>('[data-player-count-in]');
    if (countOption) queueMicrotask(closeDrawer);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !drawer.hidden) closeDrawer();
  };

  click.addEventListener('click', openClickSettings);
  drawer.addEventListener('click', onDrawerClick);
  drawer.addEventListener('input', onDrawerInput);
  drawerClose.addEventListener('click', closeDrawer);
  drawerScrim.addEventListener('click', closeDrawer);
  countIn.addEventListener('click', onCountInCapture, true);
  loop.addEventListener('click', onLoopCapture, true);
  more.addEventListener('click', onMoreCapture, true);
  document.addEventListener('keydown', onKeyDown);

  row.classList.add('player-transport-utilities-ready');
  countIn.classList.add('player-transport-utility', 'player-count-in-utility');
  loop.classList.add('player-transport-utility', 'player-loop-utility');
  more.classList.add('player-transport-utility', 'player-more-utility');
  previous.classList.add('player-transport-core-secondary');
  next.classList.add('player-transport-core-secondary');

  row.append(countIn, loop, previous, play, next, click, more);

  const refresh = (): void => {
    const countActive = countInIsActive();
    countIn.classList.toggle('is-active', countActive);
    countIn.removeAttribute('data-badge');
    countIn.disabled = activePlayerContext().song == null;
    countIn.innerHTML = `<span class="player-utility-icon">${CLOCK_ICON}</span>`;
    countIn.title = countActive ? 'Count-in on' : 'Count-in off';
    countIn.setAttribute('aria-label', `${countIn.title}. Open count-in options`);

    const hasClick = songHasClick();
    click!.classList.toggle('is-active', hasClick);
    click!.innerHTML = `<span class="player-utility-icon">${METRONOME_ICON}</span>`;
    click!.title = hasClick ? 'Click on' : 'Click off';
    click!.setAttribute('aria-label', `${click!.title}. Open click settings`);

    const currentLoopMode = loopMode();
    loop.classList.toggle('is-active', currentLoopMode !== 'Off');
    loop.removeAttribute('data-badge');
    loop.title = currentLoopMode === 'Off' ? 'Loop off' : 'Loop on';
    loop.setAttribute('aria-label', `${loop.title}. Open loop options`);

    more.title = 'Player options';
    more.setAttribute('aria-label', 'Player options');
  };

  refresh();

  const unsubscribe = appStore.subscribe(refresh);
  const loopObserver = new MutationObserver(refresh);
  if (loopLabel) loopObserver.observe(loopLabel, { childList: true, characterData: true, subtree: true });

  return () => {
    unsubscribe();
    loopObserver.disconnect();
    click?.removeEventListener('click', openClickSettings);
    drawer.removeEventListener('click', onDrawerClick);
    drawer.removeEventListener('input', onDrawerInput);
    drawerClose.removeEventListener('click', closeDrawer);
    drawerScrim.removeEventListener('click', closeDrawer);
    countIn.removeEventListener('click', onCountInCapture, true);
    loop.removeEventListener('click', onLoopCapture, true);
    more.removeEventListener('click', onMoreCapture, true);
    document.removeEventListener('keydown', onKeyDown);
    drawer.remove();
    click?.remove();
  };
}
